// src/stage2/characters/PathMotion.jsx
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

/* ---------- util: smooth yaw ---------- */
function lerpAngle(a, b, t) {
  let delta = b - a;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return a + delta * t;
}

/**
 * PathMotion (forwardRef)
 * - moves its <group> along a path
 * - optionally rotates to face direction of travel
 * - "paused" freezes time progression AND yaw updates
 */
const PathMotion = React.forwardRef(function PathMotion(
  {
    children,
    center = [1, 0, -9],
    radius = 1.2,
    speed = 0.25,
    height = 0,
    phase = 0,
    shape = "circle",
    oval = [1.6, 0.9],
    faceLerp = 14,
    modelYawOffset = 0,
    paused = false,
  },
  forwardedRef
) {
  const innerRef = useRef();
  const ref = forwardedRef || innerRef;

  const tRef = useRef(phase);
  const pos = useRef(new THREE.Vector3());
  const prev = useRef(new THREE.Vector3());
  const dir = useRef(new THREE.Vector3());
  const targetYaw = useRef(0);

  useEffect(() => {
    prev.current.set(center[0], center[1] + height, center[2]);
  }, [center, height]);

  useFrame((_, dt) => {
    if (!ref.current) return;

    // freeze path when paused
    if (!paused) tRef.current = (tRef.current + speed * dt) % 1;

    const t = tRef.current * Math.PI * 2;

    const cx = center[0];
    const cy = center[1] + height;
    const cz = center[2];

    if (shape === "figure8") {
      const x = Math.sin(t) * radius;
      const z = Math.sin(t) * Math.cos(t) * radius * 1.4;
      pos.current.set(cx + x, cy, cz + z);
    } else if (shape === "oval") {
      const rx = oval[0] ?? radius;
      const rz = oval[1] ?? radius * 0.7;
      pos.current.set(cx + Math.cos(t) * rx, cy, cz + Math.sin(t) * rz);
    } else {
      pos.current.set(cx + Math.cos(t) * radius, cy, cz + Math.sin(t) * radius);
    }

    dir.current.copy(pos.current).sub(prev.current);
    dir.current.y = 0;

    ref.current.position.copy(pos.current);

    // rotate only when not paused
    if (!paused && dir.current.lengthSq() > 1e-6) {
      dir.current.normalize();
      targetYaw.current = Math.atan2(dir.current.x, dir.current.z) + modelYawOffset;

      const a = 1 - Math.exp(-faceLerp * dt);
      ref.current.rotation.y = lerpAngle(ref.current.rotation.y, targetYaw.current, a);
    }

    prev.current.copy(pos.current);
  });

  return <group ref={ref}>{children}</group>;
});

export default PathMotion;
